import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardChartCardKebabMenu from "./DashboardChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { mergeAgeAndGenderModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";

const AGE_GENDER_PALETTE = {
  female: "#FC84B4",
  male: "#4E8EF0",
  group: "#5CC7C1",
  other: "#B2B3F5",
};

/** Left-to-right stack order (Figma layered bars). */
const STACK_KEYS = /** @type {const} */ (["group", "other", "male", "female"]);

function formatInt(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

/**
 * @param {import("./dashboardChartsModel.js").AgeGenderRow} row
 */
function rowTotal(row) {
  return row.female + row.male + row.group + row.other;
}

/** Tooltip row order (Figma). */
const TOOLTIP_KEYS = /** @type {const} */ (["female", "male", "other", "group"]);

function formatPct(count, total) {
  if (!total || !Number.isFinite(count)) return "0.00";
  return ((count / total) * 100).toFixed(2);
}

/**
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").AgeGenderRow} props.rowRaw
 * @param {string} props.ageLabel
 * @param {number} props.total
 * @param {import("i18next").TFunction} props.t
 */
function AgeGenderBarTooltip({ rowRaw, ageLabel, total, t }) {
  return (
    <div className="pos-age-gender-tooltip" role="tooltip">
      <div className="pos-age-gender-tooltip__head">
        <span className="pos-age-gender-tooltip__title">{ageLabel}</span>
        <span className="pos-age-gender-tooltip__total">
          {formatInt(total)} {t("people", { defaultValue: "people" })}
        </span>
      </div>
      <div className="pos-age-gender-tooltip__divider" aria-hidden />
      <ul className="pos-age-gender-tooltip__list">
        {TOOLTIP_KEYS.map((key) => {
          const count = rowRaw[key];
          const pct = formatPct(count, total);
          const label =
            key === "female"
              ? t("Female", { defaultValue: "Female" })
              : key === "male"
                ? t("Male", { defaultValue: "Male" })
                : key === "other"
                  ? t("Others", { defaultValue: "Others" })
                  : t("Group", { defaultValue: "Group" });
          return (
            <li key={key} className="pos-age-gender-tooltip__row">
              <span className="pos-age-gender-tooltip__dot" style={{ background: AGE_GENDER_PALETTE[key] }} aria-hidden />
              <span className="pos-age-gender-tooltip__line">
                <span className="pos-age-gender-tooltip__label">{label}:</span>
                <strong className="pos-age-gender-tooltip__val">{formatInt(count)}</strong>
                <span className="pos-age-gender-tooltip__pct"> ({pct}%)</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").AgeAndGenderModel }} props
 */
function AgeGenderStackedBody({ model }) {
  const { t } = useTranslation();
  const [hoverKey, setHoverKey] = useState(/** @type {string | null} */ (null));

  const rows = useMemo(() => {
    const list = model?.rows ?? [];
    return list.map((r, i) => {
      const total = rowTotal(r);
      const parts = STACK_KEYS.map((key) => ({
        key: `${key}-${i}`,
        k: key,
        value: r[key],
        color: AGE_GENDER_PALETTE[key],
      })).filter((p) => p.value > 0);
      return {
        key: `age-row-${i}`,
        ageLabel: r.ageLabel,
        raw: r,
        total,
        parts,
      };
    });
  }, [model]);

  const legend = useMemo(
    () => [
      { key: "female", label: t("Female", { defaultValue: "Female" }), color: AGE_GENDER_PALETTE.female },
      { key: "male", label: t("Male", { defaultValue: "Male" }), color: AGE_GENDER_PALETTE.male },
      { key: "group", label: t("Group", { defaultValue: "Group" }), color: AGE_GENDER_PALETTE.group },
      { key: "other", label: t("Others", { defaultValue: "Others" }), color: AGE_GENDER_PALETTE.other },
    ],
    [t],
  );

  return (
    <div className="pos-age-gender-stack" aria-label={t("graphAgeAndGender", { defaultValue: "Age and Gender" })}>
      <div className="pos-age-gender-stack__rows">
        {rows.map((row) => (
          <div
            key={row.key}
            className="pos-age-gender-stack__row"
            onMouseEnter={() => setHoverKey(row.key)}
            onMouseLeave={() => setHoverKey(null)}
          >
            <div className="pos-age-gender-stack__head">
              <span className="pos-age-gender-stack__age">{row.ageLabel}</span>
              <span className="pos-age-gender-stack__total">
                {formatInt(row.total)} {t("people", { defaultValue: "people" })}
              </span>
            </div>
            <div className="pos-age-gender-stack__track">
              {row.total > 0 ? (
                <div className="pos-age-gender-stack__fill" role="img" aria-label={row.ageLabel}>
                  {row.parts.map((p) => (
                    <div
                      key={p.k}
                      className="pos-age-gender-stack__segment"
                      style={{
                        flexGrow: p.value,
                        backgroundColor: p.color,
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="pos-age-gender-stack__fill pos-age-gender-stack__fill--empty" aria-hidden />
              )}
            </div>
            {hoverKey === row.key ? (
              <AgeGenderBarTooltip rowRaw={row.raw} ageLabel={row.ageLabel} total={row.total} t={t} />
            ) : null}
          </div>
        ))}
      </div>
      <div className="pos-age-gender-stack__legend" role="list">
        {legend.map((item) => (
          <div key={item.key} className="pos-age-gender-stack__legend-item" role="listitem">
            <span className="pos-age-gender-stack__dot" style={{ background: item.color }} aria-hidden />
            <span className="pos-age-gender-stack__legend-label">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").AgeAndGenderModel }} props
 */
function AgeGenderTableBody({ model }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {"age"|"female"|"male"|"group"|"other"} */ ("age"),
    dir: /** @type {"asc"|"desc"} */ ("asc"),
  }));

  useEffect(() => {
    setSort({ key: "age", dir: "asc" });
  }, [model]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const baseRows = useMemo(() => {
    const list = model?.rows ?? [];
    return list.map((r, i) => ({
      key: `age-row-${i}-${r.ageLabel}`,
      ageLabel: r.ageLabel,
      female: r.female,
      male: r.male,
      group: r.group,
      other: r.other,
    }));
  }, [model]);

  const rows = useMemo(() => {
    const copy = baseRows.map((r) => ({ ...r }));
    const mul = sort.dir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let c = 0;
      switch (sort.key) {
        case "age":
          c = String(a.ageLabel).localeCompare(String(b.ageLabel), undefined, { sensitivity: "base" });
          break;
        case "female":
          c = a.female - b.female;
          break;
        case "male":
          c = a.male - b.male;
          break;
        case "group":
          c = a.group - b.group;
          break;
        case "other":
          c = a.other - b.other;
          break;
        default:
          c = 0;
      }
      return c * mul;
    });
    return copy;
  }, [baseRows, sort]);

  const thBase = "pos-age-gender-table__th";
  const thSorted = "pos-age-gender-table__th--sorted";

  return (
    <div className="pos-age-gender-table" role="table" aria-label={t("graphAgeAndGender", { defaultValue: "Age and Gender" })}>
      <div className="pos-age-gender-table__head" role="rowgroup">
        <div className="pos-age-gender-table__tr pos-age-gender-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-age-gender-table__th--age"
            ariaSortLabel={t("Sort by age", { defaultValue: "Sort by age" })}
            sorted={sort.key === "age"}
            direction={sort.dir}
            onSort={() => toggleSort("age")}
          >
            {t("Age", { defaultValue: "Age" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-age-gender-table__th--num pos-age-gender-table__th--female"
            ariaSortLabel={t("Sort by female count", { defaultValue: "Sort by female count" })}
            sorted={sort.key === "female"}
            direction={sort.dir}
            onSort={() => toggleSort("female")}
          >
            {t("Female", { defaultValue: "Female" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-age-gender-table__th--num pos-age-gender-table__th--male"
            ariaSortLabel={t("Sort by male count", { defaultValue: "Sort by male count" })}
            sorted={sort.key === "male"}
            direction={sort.dir}
            onSort={() => toggleSort("male")}
          >
            {t("Male", { defaultValue: "Male" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-age-gender-table__th--num pos-age-gender-table__th--group"
            ariaSortLabel={t("Sort by group count", { defaultValue: "Sort by group count" })}
            sorted={sort.key === "group"}
            direction={sort.dir}
            onSort={() => toggleSort("group")}
          >
            {t("Group", { defaultValue: "Group" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-age-gender-table__th--num pos-age-gender-table__th--other pos-age-gender-table__th--last"
            ariaSortLabel={t("Sort by other count", { defaultValue: "Sort by other count" })}
            sorted={sort.key === "other"}
            direction={sort.dir}
            onSort={() => toggleSort("other")}
          >
            {t("Other", { defaultValue: "Other" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-age-gender-table__scroll" role="rowgroup">
        {rows.map((r) => (
          <div key={r.key} className="pos-age-gender-table__tr" role="row">
            <div className="pos-age-gender-table__td pos-age-gender-table__td--age" role="cell">
              {r.ageLabel}
            </div>
            <div className="pos-age-gender-table__td pos-age-gender-table__td--num pos-age-gender-table__td--female" role="cell">
              {formatInt(r.female)}
            </div>
            <div className="pos-age-gender-table__td pos-age-gender-table__td--num pos-age-gender-table__td--male" role="cell">
              {formatInt(r.male)}
            </div>
            <div className="pos-age-gender-table__td pos-age-gender-table__td--num pos-age-gender-table__td--group" role="cell">
              {formatInt(r.group)}
            </div>
            <div className="pos-age-gender-table__td pos-age-gender-table__td--num pos-age-gender-table__td--other" role="cell">
              {formatInt(r.other)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Age and gender — horizontal stacked bars + table; kebab (Chart, Table, Download, Remove).
 *
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").AgeAndGenderModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function AgeAndGenderChartCard({ model, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewMode, setViewMode] = useState(/** @type {"chart"|"table"} */ ("chart"));
  const merged = useMemo(() => mergeAgeAndGenderModel(model ?? null), [model]);

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("age-gender"));
  }, []);

  return (
    <div
      className={`pos-dashboard-chart-card pos-dashboard-chart-card--age-gender${
        viewMode === "table" ? " pos-dashboard-chart-card--age-gender-table" : ""
      }`}
    >
      <header className="pos-dashboard-chart-card__header pos-dashboard-chart-card__header--age-gender">
        <h2 className="pos-dashboard-chart-card__title">{t("graphAgeAndGender", { defaultValue: "Age and Gender" })}</h2>
        <div className="pos-dashboard-chart-card__header-actions">
          <DashboardChartCardKebabMenu
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onDownload={handleDownloadPng}
            onRemove={onMenuRemove}
            showViewToggle
          />
        </div>
      </header>
      <div
        className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--age-gender${
          viewMode === "table" ? " pos-dashboard-chart-card__body--age-gender-table" : ""
        }`}
      >
        <div ref={captureRef} className="pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--age-gender">
          {viewMode === "chart" ? <AgeGenderStackedBody model={merged} /> : null}
          {viewMode === "table" ? <AgeGenderTableBody model={merged} /> : null}
        </div>
      </div>
    </div>
  );
}

export default AgeAndGenderChartCard;
